import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Master from './pages/Master';
import UserAccessRights from './pages/UserAccessRights';
import AccessRightEditor from './pages/AccessRightEditor';
import UserCreation from './pages/UserCreation';
import UserList from './pages/UserList';
import EditUser from './pages/EditUser';
import EmployeeAccess from './pages/EmployeeAccess';
import EmailSettings from './pages/EmailSettings';
import ApproverSettings from './pages/ApproverSettings';
import Dashboard from './pages/Dashboard';
import KnowledgeBase from './pages/KnowledgeBase';
import Employees from './pages/Employees';
import Training from './pages/Training';
import Candidates from './pages/Candidates';
import Shifts from './pages/Shifts';
import Attendance from './pages/Attendance';
import Branches from './pages/Branches';
import LeaveTypes from './pages/LeaveTypes';
import LeaveApplications from './pages/LeaveApplications';
import Holidays from './pages/Holidays';
import ExpenseCategories from './pages/ExpenseCategories';
import ExpenseClaims from './pages/ExpenseClaims';
import AdvanceClaims from './pages/AdvanceClaims';
import AppraisalCycles from './pages/AppraisalCycles';
import AppraisalTemplates from './pages/AppraisalTemplates';
import EmployeeAppraisals from './pages/EmployeeAppraisals';
import AppraisalSettings from './pages/pas/AppraisalSettings';
import AssignAppraisal from './pages/pas/AssignAppraisal';
import ApproveAppraisal from './pages/pas/ApproveAppraisal';
import PublishAppraisal from './pages/pas/PublishAppraisal';
import AppraisalReports from './pages/pas/AppraisalReports';
import SalaryStructures from './pages/SalaryStructures';
import Payslips from './pages/Payslips';
import Sections from './pages/masters/Sections';
import DesignationGroups from './pages/masters/DesignationGroups';
import FwlMaster from './pages/masters/FwlMaster';
import DailyRates from './pages/masters/DailyRates';
import HolidayGroups from './pages/masters/HolidayGroups';
import AllowancesDeductions from './pages/masters/AllowancesDeductions';
import PayrollSystemSettings from './pages/payroll/SystemSettings';
import PayDayGroups from './pages/payroll/PayDayGroups';
import PayDayGroupWizard from './pages/payroll/PayDayGroupWizard';
import PayrollCycle from './pages/payroll/PayrollCycle';
import BankSubmission from './pages/payroll/BankSubmission';
import CpfSubmission from './pages/payroll/CpfSubmission';
import PayrollReports from './pages/payroll/Reports';
import PayrollDashboard from './pages/payroll/Dashboard';
import LeaveGroups from './pages/masters/LeaveGroups';
import LeaveCategories from './pages/masters/LeaveCategories';
import LeaveSystemSettings from './pages/LeaveSystemSettings';
import MedicalClaimSettings from './pages/masters/MedicalClaimSettings';
import ExcludeDays from './pages/masters/ExcludeDays';
import OffInLieu from './pages/OffInLieu';
import LeaveAdjustments from './pages/LeaveAdjustments';
import BlockAnnualLeave from './pages/masters/BlockAnnualLeave';
import MaternityPaternity from './pages/MaternityPaternity';
import LeaveEligibility from './pages/LeaveEligibility';
import ApplyOnBehalf from './pages/ApplyOnBehalf';
import LeaveRecalculate from './pages/LeaveRecalculate';
import YearEndProcess from './pages/YearEndProcess';
import LeaveReports from './pages/LeaveReports';
import LeaveDashboard from './pages/LeaveDashboard';
import FwaSettings from './pages/FwaSettings';
import FwaApplications from './pages/FwaApplications';
import ClaimDateRanges from './pages/masters/ClaimDateRanges';
import ClaimGroups from './pages/masters/ClaimGroups';
import ClaimLimits from './pages/masters/ClaimLimits';
import ClaimReports from './pages/ClaimReports';
import ClaimSettings from './pages/ClaimSettings';
import RatingGroups from './pages/masters/RatingGroups';
import Grades from './pages/masters/Grades';
import AppraisalTemplateBuilder from './pages/masters/AppraisalTemplateBuilder';
import ShiftWeekdays from './pages/masters/ShiftWeekdays';
import AutoShifts from './pages/masters/AutoShifts';
import DutyRosters from './pages/masters/DutyRosters';
import BranchSetup from './pages/masters/BranchSetup';
import TmsSystemParameters from './pages/tms/SystemParameters';
import Readers from './pages/tms/Readers';
import EmployeeScheduleImport from './pages/tms/EmployeeScheduleImport';
import BranchSchedule from './pages/tms/BranchSchedule';
import TmsAllowances from './pages/tms/Allowances';
import UpdateByIndividual from './pages/tms/UpdateByIndividual';
import UpdateByDepartment from './pages/tms/UpdateByDepartment';
import ReprocessRecalculate from './pages/tms/ReprocessRecalculate';
import TmsReports from './pages/tms/Reports';
import TmsDashboard from './pages/tms/Dashboard';
import HrDashboard from './pages/HrDashboard';
import FeedbackTickets from './pages/FeedbackTickets';
import MeetingRooms from './pages/MeetingRooms';
import OrgChart from './pages/OrgChart';
import StaffPersonalChanges from './pages/StaffPersonalChanges';
import EmployeePersonalDetails from './pages/EmployeePersonalDetails';
import LetterEditor from './pages/LetterEditor';
import Policies from './pages/Policies';
import Memos from './pages/Memos';
import Onboarding from './pages/Onboarding';
import CandidatePortal from './pages/CandidatePortal';
import EssPortal from './pages/EssPortal';
import './index.css';

function PrivateRoute() {
  const token = localStorage.getItem('token');
  return token ? <Outlet /> : <Navigate to="/login" />;
}

/**
 * Holds a user who owes a password change (first login, admin reset, or an
 * expired password) on /change-password. The backend blocks these sessions too
 * — this only keeps the UI from rendering pages whose data would all 423.
 */
function RequirePasswordChanged() {
  const { mustChangePassword } = useAuth();
  return mustChangePassword ? <Navigate to="/change-password" replace /> : <Outlet />;
}

function App() {
  return (
    <AuthProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/candidate-portal/:token" element={<CandidatePortal />} />

        <Route element={<PrivateRoute />}>
          {/* Outside the Layout and the gate: this is the one authenticated
              screen a user owing a password change is allowed to reach. */}
          <Route path="/change-password" element={<ChangePassword />} />

          <Route element={<RequirePasswordChanged />}>
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/hr/dashboard" element={<HrDashboard />} />
            <Route path="/hr/feedback" element={<FeedbackTickets />} />
            <Route path="/hr/meeting-rooms" element={<MeetingRooms />} />
            <Route path="/hr/org-chart" element={<OrgChart />} />
            <Route path="/hr/staff-personal-changes" element={<StaffPersonalChanges />} />
            <Route path="/hr/personal-details" element={<EmployeePersonalDetails />} />
            <Route path="/hr/letters" element={<LetterEditor />} />
            <Route path="/hr/policies" element={<Policies />} />
            <Route path="/hr/memos" element={<Memos />} />
            <Route path="/hr/onboarding-setup" element={<Onboarding />} />
            <Route path="/ess" element={<EssPortal />} />
            <Route path="/admin/master" element={<Master />} />
            <Route path="/admin/access-rights" element={<UserAccessRights />} />
            <Route path="/admin/access-rights/new" element={<AccessRightEditor />} />
            <Route path="/admin/access-rights/:id" element={<AccessRightEditor />} />
            <Route path="/admin/users/new" element={<UserCreation />} />
            <Route path="/admin/users/:id/edit" element={<EditUser />} />
            <Route path="/admin/users" element={<UserList />} />
            <Route path="/admin/employee-access" element={<EmployeeAccess />} />
            <Route path="/admin/email-settings" element={<EmailSettings />} />
            <Route path="/admin/approver-settings" element={<ApproverSettings />} />
            <Route path="/hr/employees" element={<Employees />} />
            <Route path="/hr/training" element={<Training />} />
            <Route path="/hr/candidates" element={<Candidates />} />
            <Route path="/tms/dashboard" element={<TmsDashboard />} />
            <Route path="/tms/shifts" element={<Shifts />} />
            <Route path="/tms/shifts/:id/weekdays" element={<ShiftWeekdays />} />
            <Route path="/tms/auto-shifts" element={<AutoShifts />} />
            <Route path="/tms/duty-rosters" element={<DutyRosters />} />
            <Route path="/tms/attendance" element={<Attendance />} />
            <Route path="/tms/branches" element={<Branches />} />
            <Route path="/tms/branches/:id" element={<BranchSetup />} />
            <Route path="/tms/system-parameters" element={<TmsSystemParameters />} />
            <Route path="/tms/readers" element={<Readers />} />
            <Route path="/tms/employee-schedule-import" element={<EmployeeScheduleImport />} />
            <Route path="/tms/branch-schedule" element={<BranchSchedule />} />
            <Route path="/tms/allowances" element={<TmsAllowances />} />
            <Route path="/tms/update-by-individual" element={<UpdateByIndividual />} />
            <Route path="/tms/update-by-department" element={<UpdateByDepartment />} />
            <Route path="/tms/reprocess-recalculate" element={<ReprocessRecalculate />} />
            <Route path="/tms/reports" element={<TmsReports />} />
            <Route path="/lms/leave-types" element={<LeaveTypes />} />
            <Route path="/lms/leave-groups" element={<LeaveGroups />} />
            <Route path="/lms/leave-categories" element={<LeaveCategories />} />
            <Route path="/lms/leave-applications" element={<LeaveApplications />} />
            <Route path="/lms/holidays" element={<Holidays />} />
            <Route path="/lms/dashboard" element={<LeaveDashboard />} />
            <Route path="/lms/system-settings" element={<LeaveSystemSettings />} />
            <Route path="/lms/masters/medical-claim-settings" element={<MedicalClaimSettings />} />
            <Route path="/lms/masters/exclude-days" element={<ExcludeDays />} />
            <Route path="/lms/oil-credits" element={<OffInLieu />} />
            <Route path="/lms/leave-adjustments" element={<LeaveAdjustments />} />
            <Route path="/lms/masters/block-al" element={<BlockAnnualLeave />} />
            <Route path="/lms/maternity-paternity" element={<MaternityPaternity />} />
            <Route path="/lms/leave-eligibility" element={<LeaveEligibility />} />
            <Route path="/lms/apply-on-behalf" element={<ApplyOnBehalf />} />
            <Route path="/lms/leave-recalculate" element={<LeaveRecalculate />} />
            <Route path="/lms/year-end" element={<YearEndProcess />} />
            <Route path="/lms/reports" element={<LeaveReports />} />
            <Route path="/lms/fwa/settings" element={<FwaSettings />} />
            <Route path="/lms/fwa/applications" element={<FwaApplications />} />
            <Route path="/ecs/categories" element={<ExpenseCategories />} />
            <Route path="/ecs/claims" element={<ExpenseClaims />} />
            <Route path="/ecs/advance-claims" element={<AdvanceClaims />} />
            <Route path="/ecs/masters/claim-date-ranges" element={<ClaimDateRanges />} />
            <Route path="/ecs/masters/claim-groups" element={<ClaimGroups />} />
            <Route path="/ecs/masters/claim-limits" element={<ClaimLimits />} />
            <Route path="/ecs/reports" element={<ClaimReports />} />
            <Route path="/ecs/settings" element={<ClaimSettings />} />
            <Route path="/pas/cycles" element={<AppraisalCycles />} />
            <Route path="/pas/templates" element={<AppraisalTemplates />} />
            <Route path="/pas/appraisals" element={<EmployeeAppraisals />} />
            <Route path="/pas/masters/rating-groups" element={<RatingGroups />} />
            <Route path="/pas/masters/grades" element={<Grades />} />
            <Route path="/pas/masters/templates" element={<AppraisalTemplateBuilder />} />
            <Route path="/pas/settings" element={<AppraisalSettings />} />
            <Route path="/pas/assign" element={<AssignAppraisal />} />
            <Route path="/pas/approve" element={<ApproveAppraisal />} />
            <Route path="/pas/publish" element={<PublishAppraisal />} />
            <Route path="/pas/reports" element={<AppraisalReports />} />
            <Route path="/payroll/structures" element={<SalaryStructures />} />
            <Route path="/payroll/payslips" element={<Payslips />} />
            <Route path="/masters/sections" element={<Sections />} />
            <Route path="/masters/designation-groups" element={<DesignationGroups />} />
            <Route path="/payroll/masters/fwl" element={<FwlMaster />} />
            <Route path="/payroll/masters/daily-rates" element={<DailyRates />} />
            <Route path="/payroll/masters/holiday-groups" element={<HolidayGroups />} />
            <Route path="/payroll/masters/allowances-deductions" element={<AllowancesDeductions />} />
            <Route path="/payroll/dashboard" element={<PayrollDashboard />} />
            <Route path="/payroll/system-settings" element={<PayrollSystemSettings />} />
            <Route path="/payroll/pay-day-groups" element={<PayDayGroups />} />
            <Route path="/payroll/pay-day-groups/:id" element={<PayDayGroupWizard />} />
            <Route path="/payroll/runs/:runId/cycle" element={<PayrollCycle />} />
            <Route path="/payroll/bank-submission" element={<BankSubmission />} />
            <Route path="/payroll/cpf-submission" element={<CpfSubmission />} />
            <Route path="/payroll/reports" element={<PayrollReports />} />
          </Route>
          </Route>
        </Route>

        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
